import AccessTokenStore from '../accessTokenStore'
import AuthClient from '../authClient'

const REFRESH_PATH = 'auth/refresh'


/**
 * Trades the refresh credential for a replacement access token and stores it.
 *
 * The credential is the refresh cookie unless the store holds refresh
 * credentials of its own, which it does only for a session that cannot keep
 * cookies. Those go in the request body instead, and the server rotates the
 * refresh token on every call, so the replacement it returns is stored in
 * place of the one spent.
 *
 * A refresh still on the wire when the store is cleared has been overtaken by a
 * logout, and storing its result would put a live bearer back into memory for
 * the rest of the trip out to the identity provider. The generation captured
 * before the request says whether that happened, and a result nobody wants is
 * dropped. Cancelling from the logout side instead would hold only for as long
 * as every future caller of `clear()` remembered to do it.
 *
 * @returns {Promise} resolves once the replacement token is stored, or once a
 *   token overtaken by a `clear()` has been discarded, rejects with the
 *   underlying Axios error if the refresh failed
 */
export default function refreshToken() {
  const generation = AccessTokenStore.getGeneration()
  const credentials = AccessTokenStore.getRefreshCredentials()

  return postRefresh(credentials)
    .then(({ data }) => {
      const isStillCurrent = ( AccessTokenStore.getGeneration() === generation )

      if ( !isStillCurrent ) {
        return
      }

      if ( credentials ) {
        AccessTokenStore.setRefreshCredentials({
          clientID: credentials.clientID,
          refreshToken: data.refresh_token,
        })
      }

      AccessTokenStore.setAccessToken({
        accessToken: data.access_token,
        expiresIn: data.expires_in,
      })
    })
}

function postRefresh(credentials) {
  if ( credentials ) {
    return AuthClient.post(REFRESH_PATH, {
      client_id: credentials.clientID,
      refresh_token: credentials.refreshToken,
    })
  }

  return AuthClient.post(REFRESH_PATH)
}
